import {
  FavoriteCollectionRepository,
  FavoriteCollectionService,
  FavoriteNibbleService,
  FavoriteUserRepository,
  FavoriteUserService
} from '@aipb/personal-content/data-access';
import {
  CreateEditCollectionDialogService,
  CreateNibbleToCollectionDialogService
} from '@aipb/personal-content/dialog';
import { NibbleDetailDialogService } from '@aipb/personal-content/dialog-nibble-detail';
import { NibbleListComponent } from '@aipb/personal-content/feature-nibble-list';

import {
  Collection,
  CollectionItem,
  CollectionType,
  NibbleCreateModel,
  NibbleCreateParams,
  NibbleCut
} from '@aipb/personal-content/shared/model';

import { PersonalContentRouting } from '@aipb/personal-content/shared/routing';
import { NibbleTypeIconComponent } from '@aipb/personal-content/ui';
import { DeleteCollectionUseCase } from '@aipb/personal-content/usecase';
import { ImageComponent } from '@aipb/ui/image';
import { InsideLoadingComponent } from '@aipb/ui/loading';
import { TruncatePipe } from '@aipb/ui/pipes';
import { CONFIG_APPLICATIONS } from '@aipb/util/di';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { CdkScrollableModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBar } from '@angular/material/progress-bar';
import { TranslocoModule } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { mergeMap, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'pc-plugin-favorite-list',
  templateUrl: './plugin-favorite-list.component.html',
  styleUrls: ['./plugin-favorite-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    InsideLoadingComponent,
    TranslocoModule,
    NibbleListComponent,
    NibbleTypeIconComponent,
    ImageComponent,
    TruncatePipe,
    MatMenuModule,
    MatButtonModule,
    MatDividerModule,
    CdkScrollableModule,
    MatProgressBar
  ]
})
export class PluginFavoriteListComponent implements OnInit {
  @Input() items: CollectionItem[] = [];
  @Input() compact = true;
  // @ts-expect-error foo
  @Input() type: CollectionType;

  // @ts-expect-error foo
  highlightNibbleId: number = null;

  private deleteCollection = inject(DeleteCollectionUseCase);
  private applications = inject(CONFIG_APPLICATIONS);

  constructor(
    private personalContentRouting: PersonalContentRouting,
    private cdr: ChangeDetectorRef,
    private favoriteCollectionRepository: FavoriteCollectionRepository,
    private favoriteCollectionService: FavoriteCollectionService,
    private favoriteUserService: FavoriteUserService,
    private favoriteUserRepository: FavoriteUserRepository,
    private favoriteNibbleService: FavoriteNibbleService,
    private createNibbleToCollectionDialogService: CreateNibbleToCollectionDialogService,
    private nibbleDetailDialogService: NibbleDetailDialogService,
    private createEditCollectionDialogService: CreateEditCollectionDialogService
  ) {}

  ngOnInit(): void {
    if (this.type === 'favorites') {
      this.favoriteNibbleService.updateNibble$.pipe(untilDestroyed(this)).subscribe(() => {
        this.init(this.items[0]);
      });
    }
  }

  isLoading(id: string): Observable<boolean> {
    return this.favoriteCollectionRepository.selectCollectionLoading(id);
  }

  init(item?: CollectionItem): void {
    this.cdr.markForCheck();

    this.favoriteUserService
      .getInfo()
      .pipe(
        untilDestroyed(this),
        mergeMap(() => this.favoriteCollectionService.get(this.favoriteUserRepository.trashbinId))
      )
      .subscribe(() => {
        this.cdr.markForCheck();
      });

    if (item) {
      this.favoriteCollectionService.get(item.id).subscribe(() => {
        this.cdr.markForCheck();
      });
    }
  }

  onNibbleCreate(nibbleCreateModel: NibbleCreateModel, item: CollectionItem): void {
    this.init(item);
    this.highlightNibbleId = nibbleCreateModel.nibble.nibble.id;
  }

  onNibbleEdit(nibbleCut: NibbleCut, item: CollectionItem): void {
    this.init(item);
    this.highlightNibbleId = nibbleCut.nibble.id;
  }

  hasAddButton(): boolean {
    return this.favoriteCollectionService.isGeneral(this.type) || this.favoriteCollectionService.isDidactic(this.type);
  }

  onAddCollection(): void {
    this.createEditCollectionDialogService
      .open$({
        mode: 'create',
        type: this.type
      })
      .subscribe(() => {
        this.init();
      });
  }

  onAddCollectionToFavorites(collection: CollectionItem): void {
    const nibbleCreate: NibbleCreateParams = {
      type: 'COLL',
      serviceValue: collection.id,
      // @ts-expect-error foo
      service: this.applications.catalog.default['services'].collection,
      userLabel: collection.label,
      userThumb: collection.thumb
    };

    const favoriteCollection = this.favoriteUserRepository.favoriteCollections[0];

    this.createNibbleToCollectionDialogService
      .open$({
        nibbleCreate
      })
      .subscribe(() => {
        this.init(favoriteCollection);
      });
  }

  onDeleteCollection(collection: CollectionItem): void {
    this.deleteCollection.execute(<Collection>collection).subscribe(() => {
      this.init();
    });
  }

  onShowCollection(id: string) {
    this.personalContentRouting.collectionSingle(id);
  }

  isCollectionVisible(id: string): boolean {
    return this.favoriteUserRepository.isCollectionVisible(id);
  }

  // @ts-expect-error foo
  onToggle(evt, collection: CollectionItem): void {
    this.favoriteUserRepository.toggleCollection(collection.id);

    const isVisible = this.isCollectionVisible(collection.id);

    if (isVisible) {
      this.favoriteCollectionService.get(collection.id).subscribe();
    }

    this.cdr.detectChanges();
  }

  getNibbles(collection: CollectionItem): Observable<NibbleCut[]> {
    return this.favoriteCollectionRepository.selectCollection(collection.id).pipe(map((c) => c?.nibbles || []));
  }

  getCollection(collection: CollectionItem): Observable<Collection> {
    // @ts-expect-error foo
    return this.favoriteCollectionRepository.selectCollection(collection.id);
  }

  onNibbleOpen(data: [NibbleCut, number, string]): void {
    this.nibbleDetailDialogService.open({
      nibbleId: data[0].nibble.id
    });
  }

  drop(event: CdkDragDrop<string[]>) {
    const item = event.container.data[event.previousIndex];
    const collId = event.container.id;

    if (event.previousContainer === event.container) {
      this.favoriteNibbleService
        // @ts-expect-error foo
        .changePosition(item['nibble'].id, collId, event.currentIndex, event.previousIndex)
        .subscribe();
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  isFavorite(): boolean {
    return this.favoriteCollectionService.isFavorite(this.type);
  }

  isTrashbin(): boolean {
    return this.favoriteCollectionService.isTrashbin(this.type);
  }
}
